// Where the vault keeps its records: IndexedDB database `lately-vault`,
// object store `records`, keyed by name. IndexedDB (not localStorage, not the
// OPFS pool) because it is the one store reachable from the page, the core
// worker and the service worker alike, and the only one that structured-clones
// a non-extractable CryptoKey (the session key SK) without exposing its bytes.
//
// Records (see vault.ts for the shapes):
//   "header"   — VaultHeader: version, protection, passkey slots (wrapped DEK)
//   "session"  — VaultSession: SK (CryptoKey) + DEK wrapped by SK + createdAt
//   "accounts" — SealedBox: the account roster + credentials under the
//                accounts subkey of the DEK
//
// A MemoryVaultStorage backs the bun tests and any context without IndexedDB.

export interface VaultStorage {
  get<T>(key: string): Promise<T | undefined>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

export const VAULT_DB_NAME = "lately-vault";
export const VAULT_STORE_NAME = "records";
const VAULT_DB_VERSION = 1;

export class MemoryVaultStorage implements VaultStorage {
  private readonly records = new Map<string, unknown>();

  async get<T>(key: string): Promise<T | undefined> {
    return this.records.get(key) as T | undefined;
  }

  async put(key: string, value: unknown): Promise<void> {
    this.records.set(key, value);
  }

  async delete(key: string): Promise<void> {
    this.records.delete(key);
  }

  async clear(): Promise<void> {
    this.records.clear();
  }

  // Test hook.
  keys(): string[] {
    return [...this.records.keys()];
  }
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export function hasIndexedDB(): boolean {
  return typeof indexedDB !== "undefined" && typeof indexedDB.open === "function";
}

export class IndexedDBVaultStorage implements VaultStorage {
  private db: Promise<IDBDatabase> | null = null;

  constructor(private readonly name: string = VAULT_DB_NAME) {}

  private openDatabase(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = new Promise((resolve, reject) => {
        const request = indexedDB.open(this.name, VAULT_DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(VAULT_STORE_NAME)) {
            db.createObjectStore(VAULT_STORE_NAME);
          }
        };
        request.onsuccess = () => {
          const db = request.result;
          // Another tab upgrading or deleting the database: drop our handle so
          // the next call reopens.
          db.onversionchange = () => {
            db.close();
            this.db = null;
          };
          resolve(db);
        };
        request.onerror = () =>
          reject(request.error ?? new Error("could not open the vault database"));
        request.onblocked = () => reject(new Error("the vault database is blocked by another tab"));
      });
      this.db.catch(() => {
        this.db = null;
      });
    }
    return this.db;
  }

  async get<T>(key: string): Promise<T | undefined> {
    const db = await this.openDatabase();
    const tx = db.transaction(VAULT_STORE_NAME, "readonly");
    const value = await requestToPromise(tx.objectStore(VAULT_STORE_NAME).get(key));
    return value as T | undefined;
  }

  async put(key: string, value: unknown): Promise<void> {
    const db = await this.openDatabase();
    const tx = db.transaction(VAULT_STORE_NAME, "readwrite");
    tx.objectStore(VAULT_STORE_NAME).put(value, key);
    await transactionDone(tx);
  }

  async delete(key: string): Promise<void> {
    const db = await this.openDatabase();
    const tx = db.transaction(VAULT_STORE_NAME, "readwrite");
    tx.objectStore(VAULT_STORE_NAME).delete(key);
    await transactionDone(tx);
  }

  async clear(): Promise<void> {
    const db = await this.openDatabase();
    const tx = db.transaction(VAULT_STORE_NAME, "readwrite");
    tx.objectStore(VAULT_STORE_NAME).clear();
    await transactionDone(tx);
  }
}

export function createVaultStorage(): VaultStorage {
  return hasIndexedDB() ? new IndexedDBVaultStorage() : new MemoryVaultStorage();
}
